import Button from "devextreme-react/button";
import Form, { GroupItem, Item } from "devextreme-react/form";
import SelectBox from "devextreme-react/select-box";
import TextArea from "devextreme-react/text-area";
import TextBox from "devextreme-react/text-box";
import React, { useState, useEffect, useMemo } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { PhaseWorkflowType } from "../../../dtos/PhaseWorkflowType";
import { StepWorkflowType } from "../../../dtos/StepWorkflowType";
import { CommentType } from "../../../dtos/CommentType";
import { StepDto } from "../../../dtos/StepDto";
import { GrantDto } from "../../../dtos/GrantDto";
import TaskFileUploader from "../../shared/tasklist/subtasks/components/FileUploader";
import TaskFiles from "../../shared/tasklist/subtasks/components/Files";
import { mapDefaultStepToStepDto } from "../../shared/tasklist/tasks/adapters/templateMappers";
import { isHybridParentType } from "../../shared/Utils";
import WorkflowTypeBadge from "../WorkflowTypeBadge";

type Props = {
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    workflowType: PhaseWorkflowType;
    parentWorkflowType?: PhaseWorkflowType;
    grantData: GrantDto;
    xsrfToken: string;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
};

export default function TemplateDetailsTab({ task, subtask, workflowType, parentWorkflowType, grantData, xsrfToken, onSave }: Props) {
    const isTask = !!task;
    const isSubtask = !!subtask;

    const isNoneShaped = workflowType === PhaseWorkflowType.None;

    // Local state for form fields
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [instructions, setInstructions] = useState("");
    const [requestComment, setRequestComment] = useState("");
    const [subtaskWorkflowType, setSubtaskWorkflowType] = useState<StepWorkflowType | null>(null);

    // For file uploads, we need a StepDto representation
    const [stepForFiles, setStepForFiles] = useState<StepDto | null>(null);

    // Saved state for dirty tracking
    const [savedName, setSavedName] = useState("");
    const [savedDescription, setSavedDescription] = useState("");
    const [savedInstructions, setSavedInstructions] = useState("");
    const [savedRequestComment, setSavedRequestComment] = useState("");
    const [savedSubtaskWorkflowType, setSavedSubtaskWorkflowType] = useState<StepWorkflowType | null>(null);

    const isStepHybridParent = parentWorkflowType === PhaseWorkflowType.StepHybrid;
    const isHybridParent = isHybridParentType(parentWorkflowType);

    // Subtask workflow options. StepHybrid parent disallows "Phase" subtasks since the
    // parent has no rollup; subtasks must run independently (Step or None).
    const subtaskWorkflowOptions = isStepHybridParent
        ? [
            { id: StepWorkflowType.Step, name: "Step — runs its own workflow" },
            { id: StepWorkflowType.None, name: "None — simple complete/not-complete tracker" }
        ]
        : [
            { id: StepWorkflowType.Phase, name: "Phase — drives the parent task's status" },
            { id: StepWorkflowType.Step, name: "Step — runs its own workflow" },
            { id: StepWorkflowType.None, name: "None — simple complete/not-complete tracker" }
        ];

    // Under a hybrid parent the form's composition follows the subtask's selected
    // type (live from the dropdown, so the form switches as soon as the user picks).
    // Returns null when hybrid and no type is picked yet — caller hides the form.
    const effectiveWorkflowType: PhaseWorkflowType | null = (() => {
        if (!isHybridParent) return workflowType;
        switch (subtaskWorkflowType) {
            case StepWorkflowType.Phase: return PhaseWorkflowType.Phase;
            case StepWorkflowType.Step: return PhaseWorkflowType.Step;
            case StepWorkflowType.None: return PhaseWorkflowType.None;
            default: return null;
        }
    })();

    // Workflow type is locked once the subtask exists (saved). Editing afterward
    // would mean the persisted config no longer matches the type.
    const isWorkflowTypeLocked = !!subtask?.id && subtask.id !== 0;

    // Compute dirty state
    const isDirty = useMemo(() => {
        return name !== savedName ||
            description !== savedDescription ||
            instructions !== savedInstructions ||
            requestComment !== savedRequestComment ||
            subtaskWorkflowType !== savedSubtaskWorkflowType;
    }, [name, savedName, description, savedDescription, instructions, savedInstructions, requestComment, savedRequestComment, subtaskWorkflowType, savedSubtaskWorkflowType]);

    useEffect(() => {
        let newName = "";
        let newDescription = "";
        let newInstructions = "";
        let newRequestComment = "";
        let newSubtaskWorkflowType: StepWorkflowType | null = null;

        if (task) {
            newName = task.name ?? "";
            newDescription = "";
            newInstructions = "";
            newRequestComment = "";
        } else if (subtask) {
            newName = subtask.name ?? "";
            newDescription = subtask.description ?? "";
            newInstructions = subtask.instructions ?? "";
            newSubtaskWorkflowType = subtask.settings?.workflowType ?? null;
            // For templates, request comment would be in the mapped step
            const mappedStep = mapDefaultStepToStepDto(subtask);
            newRequestComment = mappedStep?.requestComment?.comment ?? "";
            setStepForFiles(mappedStep);
        }

        setName(newName);
        setDescription(newDescription);
        setInstructions(newInstructions);
        setRequestComment(newRequestComment);
        setSubtaskWorkflowType(newSubtaskWorkflowType);
        setSavedName(newName);
        setSavedDescription(newDescription);
        setSavedInstructions(newInstructions);
        setSavedRequestComment(newRequestComment);
        setSavedSubtaskWorkflowType(newSubtaskWorkflowType);
    }, [task, subtask]);

    const handleSave = async () => {
        const trimmedName = name.trim();
        const trimmedDescription = description.trim();
        const trimmedInstructions = instructions.trim();
        const trimmedRequestComment = requestComment.trim();

        if (isTask && task) {
            const updatedTask: DefaultPhaseDto = {
                ...task,
                name: trimmedName
            };
            await onSave(updatedTask);
        } else if (isSubtask && subtask) {
            // For workflow types with request/response, save requestComment to description.
            // The None workflow has no request/response, so the description saves directly.
            // Use effectiveWorkflowType so Hybrid-parent saves use the picked type.
            const descriptionValue = isNoneShaped
                ? trimmedDescription
                : trimmedRequestComment;

            const updatedSubtask: DefaultStepDto = {
                ...subtask,
                name: trimmedName,
                description: descriptionValue,
                instructions: isNoneShaped ? trimmedInstructions : subtask.instructions,
                settings: {
                    ...subtask.settings,
                    workflowType: isHybridParent ? subtaskWorkflowType : subtask.settings?.workflowType
                }
            };
            await onSave(updatedSubtask);
        }

        // Sync saved state with trimmed values
        setName(trimmedName);
        setDescription(trimmedDescription);
        setInstructions(trimmedInstructions);
        setRequestComment(trimmedRequestComment);
        setSavedName(trimmedName);
        setSavedDescription(trimmedDescription);
        setSavedInstructions(trimmedInstructions);
        setSavedRequestComment(trimmedRequestComment);
        setSavedSubtaskWorkflowType(subtaskWorkflowType);
    };

    const handleDiscard = () => {
        setName(savedName);
        setDescription(savedDescription);
        setInstructions(savedInstructions);
        setRequestComment(savedRequestComment);
        setSubtaskWorkflowType(savedSubtaskWorkflowType);
    };

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            {/* Name field with readonly workflow type badge to the right.
                Uses effectiveWorkflowType so a new subtask under a hybrid parent shows
                the placeholder until the user picks a type in the dropdown below. */}
            <div style={{ marginBottom: "16px" }}>
                <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                    {isTask ? "Task Name" : "Subtask Name"}
                </label>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <div style={{ flex: 1 }}>
                        <TextBox
                            value={name}
                            onValueChanged={(e) => setName(e.value)}
                            width="100%"
                            maxLength={50}
                        />
                    </div>
                    {isSubtask && (
                        <WorkflowTypeBadge
                            workflowType={effectiveWorkflowType}
                            prefix="Subtask Workflow"
                            placeholder={isHybridParent && effectiveWorkflowType === null ? "Pick below" : undefined}
                        />
                    )}
                </div>
            </div>

            {/* Per-subtask workflow picker — only shown when parent task is a hybrid type AND
                the subtask is new (not yet saved). Once saved the type is readonly and shown
                only via the badge next to the name above. */}
            {isSubtask && isHybridParent && !isWorkflowTypeLocked && (
                <div style={{ marginBottom: "16px" }}>
                    <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                        Subtask Workflow
                    </label>
                    <SelectBox
                        items={subtaskWorkflowOptions}
                        displayExpr="name"
                        valueExpr="id"
                        value={subtaskWorkflowType}
                        onValueChanged={(e) => setSubtaskWorkflowType(e.value)}
                        width="100%"
                        placeholder="Select a workflow..."
                    />
                </div>
            )}

            {/* Hybrid parent + new subtask without a type picked yet — prompt the user. */}
            {isSubtask && isHybridParent && effectiveWorkflowType === null && (
                <div style={{ padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px", marginBottom: "16px", color: "#666", fontSize: "14px" }}>
                    Pick a Subtask Workflow above to configure this subtask.
                </div>
            )}

            {/* Description/Request and Files - side by side layout */}
            {isSubtask && effectiveWorkflowType !== null && (
                <div style={{ display: "flex", gap: "24px", marginBottom: "16px" }}>
                    {/* Left side: Description or Request (wider) */}
                    <div style={{ flex: 3, minWidth: 0 }}>
                        {isNoneShaped ? (
                            <>
                                <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                                    Instructions
                                </label>
                                <TextArea
                                    value={instructions}
                                    onValueChanged={(e) => setInstructions(e.value)}
                                    width="100%"
                                    height={100}
                                    maxLength={2000}
                                />
                                <label style={{ fontWeight: 500, marginBottom: "4px", marginTop: "12px", display: "block" }}>
                                    Description
                                </label>
                                <TextArea
                                    value={description}
                                    onValueChanged={(e) => setDescription(e.value)}
                                    width="100%"
                                    height={200}
                                    maxLength={2000}
                                />
                            </>
                        ) : (
                            <>
                                <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                                    Request
                                </label>
                                <TextArea
                                    value={requestComment}
                                    onValueChanged={(e) => setRequestComment(e.value)}
                                    width="100%"
                                    height={200}
                                />
                            </>
                        )}
                    </div>

                    {/* Right side: Files (only when subtask is saved) */}
                    {!!subtask?.id && subtask.id !== 0 && stepForFiles && (
                        <div style={{ flex: 2, minWidth: 0 }}>
                            <label style={{ fontWeight: 500, marginBottom: "4px", display: "block" }}>
                                Files
                            </label>
                            <TaskFileUploader
                                grant={grantData}
                                step={stepForFiles}
                                xsrfToken={xsrfToken}
                                isDisabled={false}
                                fileType={isNoneShaped ? CommentType.None : CommentType.Request}
                            />
                            <TaskFiles
                                grant={grantData}
                                step={stepForFiles}
                                canDelete={true}
                                canView={true}
                                fileType={isNoneShaped ? CommentType.None : CommentType.Request}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Task-specific info */}
            {isTask && (
                <div style={{ padding: "12px", backgroundColor: "#f5f5f5", borderRadius: "4px", marginBottom: "16px" }}>
                    <p style={{ margin: 0, color: "#666", fontSize: "14px" }}>
                        Task-level details are configured in the Settings tab.
                        Use this tab to edit the task name and view subtasks.
                    </p>
                </div>
            )}

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save *" : "Save"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                />
                {isDirty && (
                    <Button
                        text="Discard Changes"
                        type="normal"
                        stylingMode="outlined"
                        onClick={handleDiscard}
                    />
                )}
                {isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "12px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>
        </div>
    );
}
